import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as gameApi from '../alkebulan/game';
import { getFeedback } from '../alkebulan/game';
import type { FeedbackResult, GameState } from '../alkebulan/game';
import { alkebulanQuestions } from '../alkebulan/game_data';
import Marketplace from './Marketplace';

const gameSounds = [
  '/media/yoruba.mp3',
  '/media/african-slow-drum.mp3',
  '/media/djembe.mp3',
  '/media/alkebulan.mp3',
];

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function shuffleOptions(question: any): any {
  const options = [...question.options];
  const correct = question.answer_index;
  const map = options.map((opt, i) => ({ opt, orig: i }));
  for (let i = map.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [map[i], map[j]] = [map[j], map[i]];
  }
  const shuffledOptions = map.map((x) => x.opt);
  const newCorrect = map.findIndex((x) => x.orig === correct);
  return { ...question, options: shuffledOptions, answer_index: newCorrect, id: question.id };
}

const AlkebulanGame: React.FC = () => {
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [resultText, setResultText] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [username, setUsername] = useState('guest');
  const [feedbackGif, setFeedbackGif] = useState('');
  const [showGif, setShowGif] = useState(false);
  const [showExpAfterGif, setShowExpAfterGif] = useState(false);
  const [readyForNext, setReadyForNext] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentId, setCurrentId] = useState<string | number | null>(null);
  const [streakMemory, setStreakMemory] = useState<boolean[]>([]);
  const [streak, setStreak] = useState(0);
  const [gold, setGold] = useState(0);
  const [cobalt, setCobalt] = useState(0);
  const [uranium, setUranium] = useState(0);
  const [oilBarrels, setOilBarrels] = useState(0);
  const [countries, setCountries] = useState<string[]>([]);
  const [lives, setLives] = useState(5);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [isPortrait, setIsPortrait] = useState<boolean>(window.matchMedia('(orientation: portrait)').matches);
  const [hideRotateHint, setHideRotateHint] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('alkebulanHideRotateHint') === 'true';
    } catch {
      return false;
    }
  });

  const gifTimerRef = useRef<number | null>(null);
  const explanationTimerRef = useRef<number | null>(null);

  const navigate = useNavigate();

  const clearGifTimers = () => {
    if (gifTimerRef.current !== null) {
      window.clearTimeout(gifTimerRef.current);
      gifTimerRef.current = null;
    }
    if (explanationTimerRef.current !== null) {
      window.clearTimeout(explanationTimerRef.current);
      explanationTimerRef.current = null;
    }
  };

  const isLocked = useMemo(() => lockedUntil !== null && now < lockedUntil, [lockedUntil, now]);
  const remainingMinutes = useMemo(
    () => (lockedUntil && now < lockedUntil ? Math.ceil((lockedUntil - now) / 60000) : 0),
    [lockedUntil, now]
  );
  const lockMessage = isLocked ? `Out of lives. Try again in ${remainingMinutes} minute(s).` : '';

  const imageUrl = '/alkebulan/alkebulan.png';

  const displayImage = !showResult ? imageUrl : feedbackGif ? feedbackGif : imageUrl;

  // Music
  useEffect(() => {
    if (audioElement) {
      audioElement.muted = isMuted;
    }
  }, [isMuted, audioElement]);

  const toggleMute = () => {
    setIsMuted((m) => !m);
  };

  const getRandomSound = () => gameSounds[Math.floor(Math.random() * gameSounds.length)];

  const playBackgroundTrack = () => {
    if (!audioElement) return;
    audioElement.src = getRandomSound();
    audioElement.loop = true;
    audioElement.play().catch((err) => console.log('Audio playback error:', err));
  };

  const startBackgroundMusic = () => {
    if (!audioElement) {
      const audio = new Audio();
      audio.volume = 0.15;
      audio.muted = isMuted;
      setAudioElement(audio);
      return;
    }

    if (!isMusicPlaying) {
      setIsMusicPlaying(true);
      playBackgroundTrack();
    }
  };

  const stopBackgroundMusic = () => {
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
    }
    setIsMusicPlaying(false);
  };

  const playGameEndSound = () => {
    const audio = new Audio('/media/game-end.mp3');
    audio.volume = 0.6;
    audio.play().catch((err) => console.log('Game end sound error:', err));
  };

  const playApplauseSound = () => {
    const audio = new Audio('/media/applause.mp3');
    audio.volume = 0.6;
    audio.play().catch((err) => console.log('Applause sound error:', err));
  };

  const loadQuestions = useCallback(async () => {
    try {
      const state = await gameApi.loadGameState(username);
      const answeredIds = state?.answers?.map((a) => a.id) ?? [];
      const allQs = alkebulanQuestions
        .filter((q) => q.question && q.options && q.answer_index !== undefined)
        .map((q) => shuffleOptions(q));
      const remaining = shuffleArray(allQs.filter((q) => !answeredIds.includes(q.id)));
      setQuestions(remaining);
      if (remaining.length === 0) {
        console.error('No valid questions found in alkebulanQuestions.');
      }
    } catch (err) {
      console.error('Error loading questions:', err);
      setQuestions([]);
    }
  }, [username]);

  const loadState = useCallback(async () => {
    const loaded = (await gameApi.loadGameState(username)) as GameState | null;
  const state: GameState = loaded || { username, answers: [] };
    if (typeof state.lives === 'number') setLives(state.lives);
    if (typeof state.lockedUntil === 'number') setLockedUntil(state.lockedUntil);
    if (typeof state.gold === 'number') setGold(state.gold);
    if (typeof state.cobalt === 'number') setCobalt(state.cobalt);
    if (typeof state.uranium === 'number') setUranium(state.uranium);
    if (typeof state.oil === 'number') setOilBarrels(state.oil);
    if (Array.isArray(state.countries)) setCountries(state.countries);

    if (state.lockedUntil && Date.now() >= state.lockedUntil) {
      setLockedUntil(null);
      setLives(5);
      await gameApi.saveGameState({
        ...state,
        lives: 5,
        lockedUntil: undefined,
        gold: state.gold ?? 0,
        cobalt: state.cobalt ?? 0,
        uranium: state.uranium ?? 0,
        oil: state.oil ?? 0,
      });
    }
    setCurrentIndex(0);
  }, [username]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const storedUser = window.localStorage?.getItem('currentUser');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUsername(parsed?.username ?? 'guest');
      } catch {
        setUsername('guest');
      }
    }

    loadQuestions();
    loadState();

    const startMusicOnInteraction = () => {
      startBackgroundMusic();
      window.removeEventListener('click', startMusicOnInteraction);
      window.removeEventListener('keydown', startMusicOnInteraction);
    };

    window.addEventListener('click', startMusicOnInteraction);
    window.addEventListener('keydown', startMusicOnInteraction);

    return () => {
      window.removeEventListener('click', startMusicOnInteraction);
      window.removeEventListener('keydown', startMusicOnInteraction);
      stopBackgroundMusic();
    };
  }, [loadQuestions, loadState]);

  useEffect(() => {
    const mql = window.matchMedia('(orientation: portrait)');
    const onOrientationChange = (event: MediaQueryListEvent) => setIsPortrait(event.matches);
    setIsPortrait(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onOrientationChange);
    } else {
      mql.addListener(onOrientationChange);
    }

    const timer = setInterval(() => {
      const nowTs = Date.now();
      setNow(nowTs);
      if (lockedUntil && nowTs >= lockedUntil) {
        setLockedUntil(null);
        setLives(5);

        gameApi
          .loadGameState(username)
          .then((prevState) => {
            const prev = prevState || { username, answers: [] };
            prev.lives = 5;
            prev.lockedUntil = undefined;
            return gameApi.saveGameState(prev);
          })
          .catch((err) => console.error(err));
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      if (mql.removeEventListener) {
        mql.removeEventListener('change', onOrientationChange);
      } else {
        mql.removeListener(onOrientationChange);
      }
    };
  }, [lockedUntil, username]);

  const handleMarketplaceUpdate = async (e: { gold?: number; cobalt?: number; uranium?: number; oil?: number; lives?: number; countries?: string[] }) => {
    const prev = { gold, lives, cobalt, uranium, oil: oilBarrels, countries: [...countries] };

    const nextGold = typeof e.gold === 'number' ? e.gold : gold;
    const nextLives = typeof e.lives === 'number' ? e.lives : lives;
    const nextCobalt = typeof e.cobalt === 'number' ? e.cobalt : cobalt;
    const nextUranium = typeof e.uranium === 'number' ? e.uranium : uranium;
    const nextOil = typeof e.oil === 'number' ? e.oil : oilBarrels;
    const nextCountries = Array.isArray(e.countries) ? e.countries : countries;

    setGold(nextGold);
    setLives(nextLives);
    setCobalt(nextCobalt);
    setUranium(nextUranium);
    setOilBarrels(nextOil);
    setCountries(nextCountries);

    const delta: any = { username };
    if (nextGold !== prev.gold) delta.gold = nextGold - prev.gold;
    if (nextCobalt !== prev.cobalt) delta.cobalt = nextCobalt - prev.cobalt;
    if (nextUranium !== prev.uranium) delta.uranium = nextUranium - prev.uranium;
    if (nextOil !== prev.oil) delta.oil_barrels = nextOil - prev.oil;
    if (JSON.stringify(nextCountries) !== JSON.stringify(prev.countries)) {
      const added = nextCountries.filter((c) => !prev.countries.includes(c));
      if (added.length) delta.countries = added;
    }

    if (Object.keys(delta).length > 1) {
      await gameApi.updateResources(delta);
    }

    const state = (await gameApi.loadGameState(username)) || { username, answers: [] };
    state.gold = nextGold;
    state.lives = nextLives;
    state.cobalt = nextCobalt;
    state.uranium = nextUranium;
    state.oil = nextOil;
    state.countries = nextCountries;
    await gameApi.saveGameState(state);
  };

  const selectAnswer = async (idx: number) => {
    if (isLocked || selected !== null || questions.length === 0) return;
    clearGifTimers();

    setReadyForNext(false);
    setSelected(idx);
    const question = questions[currentIndex];
    const correct = idx === question.answer_index;
    setResultText(correct ? 'Correct!' : 'Incorrect!');
    setShowResult(true);
    setIsFlipped(true);
    setShowExpAfterGif(false);
    setShowGif(true);

    setCurrentId(question.id);

    let dbState = (await gameApi.loadGameState(username)) as GameState;
    if (!dbState) {
      dbState = { username, answers: [] };
    }
    if (!dbState.answers) dbState.answers = [];

    let newStreakMemory = [...streakMemory];
    if (correct) {
      setOilBarrels((oldOil) => oldOil + 1);
      newStreakMemory.push(true);
      if (newStreakMemory.length > 5) {
        newStreakMemory.shift();
      }
    } else {
      newStreakMemory = [];
    }

    setStreakMemory(newStreakMemory);
    const streakValue = newStreakMemory.filter((x) => x).length === newStreakMemory.length ? newStreakMemory.length : 0;
    setStreak(streakValue);

    const feedback: FeedbackResult = getFeedback(newStreakMemory, correct);
    setFeedbackGif(feedback.flipGif);

    let goldDelta = 0;
    let cobaltDelta = 0;
    let uraniumDelta = 0;
    let oilDelta = 0;

    if (feedback.reward === 'gold') {
      goldDelta = feedback.rewardAmount || 1;
      setGold((prev) => prev + goldDelta);
    }
    if (feedback.reward === 'cobalt') {
      cobaltDelta = feedback.rewardAmount || 1;
      setCobalt((prev) => prev + cobaltDelta);
    }
    if (feedback.reward === 'uranium') {
      uraniumDelta = feedback.rewardAmount || 1;
      setUranium((prev) => prev + uraniumDelta);
      playApplauseSound();
    }
    if (correct) {
      oilDelta = 1;
      // already incremented above with setOilBarrels
    }

    let newLives = lives;
    let newLockedUntil = lockedUntil;

    if (!correct) {
      newLives = Math.max(0, lives - 1);
      setLives(newLives);
      if (newLives === 0) {
        newLockedUntil = Date.now() + 10 * 60 * 1000;
        setLockedUntil(newLockedUntil);
        playGameEndSound();
      }
    }

    if (correct) {
      setLives(lives);
    }

    if (correct && !dbState.answers.some((a) => a.id === question.id)) {
      dbState.answers.push({ id: question.id });
    }

    const toSave: GameState = (await gameApi.loadGameState(username)) || { username, answers: [] };
    toSave.lives = newLives;
    toSave.lockedUntil = (newLockedUntil === null ? undefined : newLockedUntil) as number | undefined;
    toSave.gold = gold + goldDelta;
    toSave.cobalt = cobalt + cobaltDelta;
    toSave.uranium = uranium + uraniumDelta;
    toSave.oil = oilBarrels + (oilDelta || 0);
    toSave.countries = countries;
    if (dbState.answers) toSave.answers = dbState.answers;
    await gameApi.saveGameState(toSave);

    if (goldDelta || cobaltDelta || uraniumDelta || oilDelta) {
      const delta: any = { username };
      if (goldDelta) delta.gold = goldDelta;
      if (cobaltDelta) delta.cobalt = cobaltDelta;
      if (uraniumDelta) delta.uranium = uraniumDelta;
      if (oilDelta) delta.oil_barrels = oilDelta;
      await gameApi.updateResources(delta);
    }

    if (correct) {
      gifTimerRef.current = window.setTimeout(() => {
        setShowGif(false);
        setFeedbackGif('');

        if (question.explanation && question.explanation.trim() !== '') {
          setShowExpAfterGif(true);
          setIsFlipped(true);

          explanationTimerRef.current = window.setTimeout(() => {
            setShowExpAfterGif(false);
            setIsFlipped(false);
            setReadyForNext(true);
          }, 4000);
        } else {
          setIsFlipped(false);
          setReadyForNext(true);
        }
      }, 2000);
    } else {
      // Keep the wrong animation for 2s then return to normal
      gifTimerRef.current = window.setTimeout(() => {
        setFeedbackGif('');
        setIsFlipped(false);
        setShowGif(false);
        setStreak(0);
        setStreakMemory([]);
        setReadyForNext(true);
      }, 2000);
    }
  };

  const nextQuestion = () => {
    clearGifTimers();
    setSelected(null);
    setShowResult(false);
    setShowExplanation(false);
    setIsFlipped(false);
    setShowExpAfterGif(false);
    setReadyForNext(false);
    setCurrentIndex((prev) => (questions.length > 0 ? (prev + 1) % questions.length : 0));
  };

  const close = () => {
    navigate('/');
  };

  useEffect(() => {
    if (isLocked && remainingMinutes === 0) {
      setLockedUntil(null);
      setLives(5);
    }
  }, [isLocked, remainingMinutes]);

  return (
    <div>
      {isPortrait && !hideRotateHint && (
        <div className="rotate-hint">
          <span>📱 Rotate to landscape for best experience</span>
          <button className="dismiss-hint" onClick={() => {
            setHideRotateHint(true);
            try { window.localStorage.setItem('alkebulanHideRotateHint', 'true'); } catch {}
          }}>Don't show again</button>
        </div>
      )}
      <div className="background-overlay"></div>
      <div className="top-bar">
        <button className="marketplace-btn" onClick={() => setShowMarketplace(true)}>
          Marketplace
        </button>
        <button className="mute-btn" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
          {isMuted ? '🔇' : '🔊'}
        </button>
        <div className="resource-bar">
          <div className="resource lives">
            <span className="icon">❤️</span> <span className="count">{lives}</span>
          </div>
          <div className="resource oil">
            <span className="icon">🛢️</span> <span className="count">{oilBarrels}</span>
          </div>
          <div className="resource gold">
            <span className="icon">🥇</span> <span className="count">{gold}</span>
          </div>
          <div className="resource cobalt">
            <span className="icon">🔵</span> <span className="count">{cobalt}</span>
          </div>
          <div className="resource uranium">
            <span className="icon">☢️</span> <span className="count">{uranium}</span>
          </div>
        </div>
      </div>

      <Marketplace
        open={showMarketplace}
        onClose={() => setShowMarketplace(false)}
        gold={gold}
        cobalt={cobalt}
        uranium={uranium}
        oil={oilBarrels}
        lives={lives}
        countries={countries}
        onUpdate={handleMarketplaceUpdate}
      />

      <div className="main-flex">
        <div className="game-container expanded">
          <button className="close-btn" aria-label="Close" onClick={close}>
            Close
          </button>
          <div className="question-box expanded">
            {questions.length > 0 ? (
              <>
                {isLocked && <div className="lock-banner">{lockMessage}</div>}
                <div className="question-text">{questions[currentIndex]?.question}</div>
                <div className="button-column expanded">
                  {questions[currentIndex]?.options?.map((opt: string, i: number) => (
                    <button
                      key={i}
                      className={`answer-btn ${selected === i ? (selected !== null && selected !== questions[currentIndex].answer_index ? 'selected wrong' : 'selected') : ''}`}
                      onClick={() => selectAnswer(i)}
                      disabled={selected !== null || isLocked}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {showExplanation && questions[currentIndex]?.explanation && (
                  <div className="explanation">{questions[currentIndex].explanation}</div>
                )}
                {showResult && readyForNext && (
                  <button className="next-btn" onClick={nextQuestion} disabled={isLocked}>
                    Next
                  </button>
                )}
              </>
            ) : (
              <div>Loading questions...</div>
            )}
          </div>
        </div>

        <div className="image-box expanded">
          <div className={`flip-container ${isFlipped ? 'flipped' : ''}`}>
            <div className="flipper">
              <div className="front">
                <img src={displayImage} alt="Result" />
              </div>
              <div className="back">
                {showGif ? (
                  <img src={displayImage} alt="Result Gif" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : showExpAfterGif ? (
                  <div className="explanation-on-image">{questions[currentIndex]?.explanation}</div>
                ) : (
                  <div className="explanation-on-image">&nbsp;</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .background-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: url('/alkebulan/background.png') center center/cover no-repeat; opacity: 0.13; z-index: 0; }
        .main-flex { display: flex; flex-direction: row; align-items: flex-start; justify-content: center; min-height: 100vh; position: relative; z-index: 1; gap: 2vw; margin-top: 50px; padding-top: 0.5rem; }
        .game-container { display: flex; flex-direction: column; align-items: stretch; justify-content: flex-start; min-width: 400px; max-width: 520px; margin-left: 2vw; margin-top: 6vh; position: relative; flex: 1 1 60%; }
        .game-container.expanded { min-width: 480px; max-width: 700px; flex: 1 1 65%; }
        .close-btn { position: fixed; top: 69px; right: 24px; background: #f87171; color: #fff; border: none; border-radius: 8px; padding: 4px 14px; font-size: 0.95rem; font-weight: 600; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.08); transition: background 0.2s; z-index: 10; }
        .close-btn:hover { background: #ef4444; }
        .question-box { background: rgba(255,255,255,0.95); border-radius: 18px; box-shadow: 0 4px 16px rgba(0,0,0,0.13); padding: 40px 32px; margin-top: 0; display: flex; flex-direction: column; align-items: stretch; min-width: 320px; max-width: 100%; width: 100%; }
        .question-box.expanded { min-width: 420px; max-width: 100%; width: 100%; padding: 48px 40px; }
        .question-text { font-size: 1.5rem; font-weight: 600; margin-bottom: 24px; color: #333; text-align: center; }
        .button-column { display: flex; flex-direction: column; gap: 20px; width: 100%; margin-bottom: 20px; }
        .button-column.expanded { gap: 28px; margin-bottom: 28px; }
        .lock-banner { background: #fde68a; color: #92400e; padding: 10px 16px; border-radius: 8px; margin-bottom: 16px; text-align: center; font-weight: 600; }
        .answer-btn { background: linear-gradient(90deg, #fbbf24, #f97316); color: #fff; border: none; border-radius: 12px; padding: 12px 0; font-size: 1.1rem; font-weight: 500; cursor: pointer; transition: background 0.2s; width: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.08); outline: none; }
        .answer-btn.selected { background: linear-gradient(90deg, #22c55e, #16a34a); border: 2.5px solid #22c55e; color: #fff; opacity: 1; box-shadow: 0 2px 12px rgba(34,197,94,0.13); }
        .answer-btn.selected.wrong { background: linear-gradient(90deg, #f87171, #ef4444); border: 2.5px solid #ef4444; color: #fff; opacity: 1; box-shadow: 0 2px 12px rgba(239,68,68,0.13); }
        .answer-btn { color: #000 !important; }
        .answer-btn:disabled { opacity: 0.7; border: 2px solid #f59e42; }
        .answer-btn:hover:not(:disabled) { border: 2.5px solid #ef4444; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
        .next-btn { margin-top: 18px; background: #38bdf8; color: #fff; border: none; border-radius: 10px; padding: 10px 24px; font-size: 1rem; font-weight: 600; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.08); transition: background 0.2s; }
        .next-btn:hover { background: #0ea5e9; }
        .image-box { margin-right: 2vw; margin-top: 6vh; display: flex; align-items: center; justify-content: center; min-width: 180px; max-width: 320px; width: 22vw; aspect-ratio: 1 / 1; background: #fff; border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.12); overflow: hidden; flex: 1 1 30%; }
        .image-box.expanded { min-width: 220px; max-width: 380px; width: 28vw; aspect-ratio: 1 / 1; }
        .flip-container { width: 100%; height: 100%; perspective: 1200px; }
        .flipper { width: 100%; height: 100%; position: relative; transform-style: preserve-3d; transition: transform 0.6s ease-in-out; }
        .flip-container.flipped .flipper { transform: rotateY(180deg); }
        .front, .back { position: absolute; top: 0; left: 0; width: 100%; height: 100%; backface-visibility: hidden; -webkit-backface-visibility: hidden; border-radius: 16px; }
        .front { z-index: 2; }
        .back { transform: rotateY(180deg); z-index: 1; }
        .image-box img, .image-box .front, .image-box .back { width: 100%; height: 100%; object-fit: contain; border-radius: 16px; background: #fff; }
        .explanation-on-image { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; height: 100%; padding: 1rem; box-sizing: border-box; text-align: center; font-size: 1.25rem; line-height: 1.4; font-weight: 700; color: #111; }
        .explanation-on-image.plain { background: #ffffff; }
        .explanation-on-image.final { background: #f9fafb; }
        .explanation { text-align: center; font-size: 0.5rem; line-height: 1.4; font-weight: 300; }
        :global(body) { margin: 0; padding: 0; overflow: hidden; font-family: 'Canva Sans', sans-serif; }
        .top-bar { width: 100vw; display: flex; flex-direction: row; align-items: center; justify-content: flex-start; position: fixed; top: 0; left: 0; z-index: 20; background: rgba(255,255,255,0.92); height: 64px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 0 2vw; }
        .marketplace-btn { background: #fbbf24; color: #000000; border: none; border-radius: 8px; padding: 10px 22px; font-size: 1.1rem; font-weight: 600; cursor: pointer; margin-right: 2vw; box-shadow: 0 2px 8px rgba(0,0,0,0.08); transition: background 0.2s; }
        .marketplace-btn:hover { background: #f59e42; }
        .mute-btn { background: #e5e7eb; color: #000; border: none; border-radius: 8px; padding: 10px 14px; font-size: 1.3rem; cursor: pointer; margin-right: 2vw; box-shadow: 0 2px 8px rgba(0,0,0,0.08); transition: background 0.2s; }
        .mute-btn:hover { background: #d1d5db; }
        .resource-bar { display: flex; flex-direction: row; align-items: center; justify-content: center; flex: 1; gap: 2vw; margin: 0 auto; }
        .resource { display: flex; align-items: center; font-size: 1.15rem; font-weight: 600; background: #fffbe6; border-radius: 10px; padding: 8px 18px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); min-width: 80px; justify-content: center; }
        .resource .icon { margin-right: 8px; font-size: 1.3rem; }

        @media (max-width: 900px) {
          .main-flex { flex-direction: column; align-items: center; gap: 1rem; margin-top: 104px; min-height: auto; padding: 0.25rem 0.5rem 1rem 0.5rem; }
          .game-container { min-width: 94vw; max-width: 96vw; margin-left: 0; margin-top: 0; }
          .question-box { padding: 18px 12px; margin-top: 0; }
          .question-text { font-size: 1.2rem; line-height: 1.3; }
          .button-column { gap: 12px; }
          .answer-btn { font-size: 1rem; padding: 10px 10px; border-radius: 10px; }
          .image-box { width: 94vw; max-width: 94vw; margin-right: 0; margin-top: 0.75rem; height: 220px; }
          .close-btn { top: 80px; right: 10px; padding: 6px 10px; font-size: 0.82rem; }
          .top-bar { flex-wrap: wrap; justify-content: space-between; gap: 0.35rem; height: auto; padding: 0.65rem 0.6rem; position: fixed; top: 0; left: 0; }
          .marketplace-btn, .mute-btn, .next-btn { font-size: 0.92rem; padding: 8px 12px; }
          .resource-bar { flex-wrap: wrap; gap: 0.35rem; margin: 0.2rem auto; }
          .resource { min-width: 70px; padding: 5px 10px; font-size: 0.95rem; }
          .image-box img, .image-box .front, .image-box .back { height: 100%; }
        }

        @media (orientation: landscape) and (max-width: 900px) {
          .main-flex { flex-direction: row; align-items: flex-start; gap: 0.6rem; margin-top: 80px; padding: 0.5rem 0.5rem 1rem 0.5rem; width: 100vw; max-height: calc(100vh - 92px); overflow: hidden; }
          .game-container { width: min(58vw, 60%); min-width: 230px; max-width: 380px; margin-top: 0; max-height: 100%; overflow-y: auto; }
          .question-box { padding: 14px 10px; max-height: calc(100vh - 160px); overflow-y: auto; display: flex; flex-direction: column; }
          .button-column { flex-shrink: 0; }
          .next-btn { margin-top: auto; position: sticky; bottom: 10px; z-index: 5; }
          .image-box { width: min(32vw, 32%); min-width: 150px; aspect-ratio: 1 / 1; margin-top: 10px; max-height: 100%; }
          .image-box img { border-radius: 10px; }
          .top-bar { position: fixed; top: 0; width: 100vw; left: 0; justify-content: space-between; }
        }

        .rotate-hint {
          position: fixed;
          top: 74px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 30;
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fffbeb;
          border: 1px solid #f59e0b;
          color: #92400e;
          padding: 6px 12px;
          border-radius: 10px;
          font-size: 0.84rem;
          font-weight: 700;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
        }
        .rotate-hint .dismiss-hint {
          background: #f59e0b;
          border: none;
          color: #fff;
          border-radius: 6px;
          padding: 2px 8px;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .rotate-hint .dismiss-hint:hover {
          background: #d97706;
        }

        @media (max-width: 640px) {
          .main-flex { padding-top: 120px; flex-direction: column; align-items: center; justify-content: flex-start; overflow-x: hidden; gap: 0.4rem; }
          .game-container { min-width: 280px; max-width: 96vw; margin-left: 0; margin-top: 0; }
          .question-box { padding: 12px 10px; margin-top: 0; max-height: calc(100vh - 150px); overflow-y: auto; }
          .question-text { font-size: 1rem; line-height: 1.3; margin-bottom: 16px; }
          .button-column { gap: 10px; }
          .marketplace-btn, .mute-btn { margin-right: 0.4rem; padding: 8px 10px; }
          .resource-bar { font-size: 0.92rem; gap: 0.3rem; }
          .resource .icon { margin-right: 6px; font-size: 1.15rem; }
          .close-btn { top: 78px; right: 8px; }
          .answer-btn { font-size: 0.9rem; padding: 8px 8px; border-radius: 10px; }
          .next-btn { font-size: 0.92rem; padding: 8px 12px; }
          .image-box { display: none; }
          .top-bar { justify-content: space-between; }
          .rotate-hint { top: 70px; font-size: 0.8rem; padding: 4px 10px; }
        }
      `}</style>
    </div>
  );
};

export default AlkebulanGame;
